import axios from 'axios';
import { currentUrl } from '../constants/global';

const SAFE_METHODS = new Set(['get', 'head', 'options', 'trace']);

const PUBLIC_AUTH_PATHS = [
  '/auth/csrf/',
  '/auth/web/login/',
  '/auth/web/google/',
  '/auth/web/refresh/',
  '/auth/logout/',
  '/auth/google/',
  '/login/',
  '/password-reset',
  '/password-reset-confirm',
  '/signup',
  '/delete-user-request',
  '/fale-conosco/',
];

export function getCookie(name) {
  if (typeof document === 'undefined') return null;
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * CSRF em memória: em produção o front (plataforma.*) e a API (api.*) são
 * cross-subdomínio — document.cookie NÃO vê o csrftoken setado pela API.
 * O backend devolve o valor em GET /auth/csrf/ → { csrfToken }.
 */
let csrfTokenMemory = null;

export function setCsrfToken(token) {
  csrfTokenMemory = token || null;
}

export function getCsrfToken() {
  return csrfTokenMemory || getCookie('csrftoken');
}

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || `${currentUrl}/api`,
  timeout: 30000000,
  withCredentials: true, // cookies HttpOnly da API
});

let refreshPromise = null;
let csrfFetchPromise = null;

function isPublicAuthUrl(url = '') {
  return PUBLIC_AUTH_PATHS.some((ep) => url.includes(ep));
}

function isSafeMethod(method = 'get') {
  return SAFE_METHODS.has(String(method).toLowerCase());
}

/** Busca CSRF do body da API e guarda em memória (não depende de document.cookie). */
export async function fetchCsrfToken() {
  if (csrfTokenMemory) return csrfTokenMemory;

  if (!csrfFetchPromise) {
    csrfFetchPromise = (async () => {
      const { data } = await api.get('/auth/csrf/');
      const token =
        data?.csrfToken || data?.csrf_token || getCookie('csrftoken') || null;
      if (token) setCsrfToken(token);
      return token;
    })().finally(() => {
      csrfFetchPromise = null;
    });
  }

  return csrfFetchPromise;
}

async function ensureCsrfHeader(config) {
  if (isSafeMethod(config.method)) return config;

  let csrf = getCsrfToken();
  if (!csrf) {
    try {
      csrf = await fetchCsrfToken();
    } catch {
      /* best effort — backend pode rejeitar com 403 */
    }
  }

  if (csrf) {
    config.headers = config.headers || {};
    config.headers['X-CSRFToken'] = csrf;
  }

  return config;
}

async function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const csrf = getCsrfToken() || (await fetchCsrfToken());
      await api.post(
        '/auth/web/refresh/',
        {},
        {
          headers: csrf ? { 'X-CSRFToken': csrf } : {},
        }
      );
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

export function clearClientAuthState() {
  csrfTokenMemory = null;
  try {
    sessionStorage.removeItem('Authorization');
    sessionStorage.removeItem('Password');
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('usuario_id');
    sessionStorage.removeItem('Email');
    localStorage.removeItem('access_token');
  } catch {
    /* ignore */
  }
}

export async function forceLogoutRedirect() {
  try {
    const csrf = getCsrfToken() || (await fetchCsrfToken().catch(() => null));
    await api.post(
      '/auth/logout/',
      {},
      { headers: csrf ? { 'X-CSRFToken': csrf } : {} }
    );
  } catch {
    /* best effort */
  }

  clearClientAuthState();

  const publicPaths = ['/', '/login', '/register', '/certificado', '/old_landing_page'];
  const path = window.location.pathname;
  if (
    !publicPaths.includes(path) &&
    !path.startsWith('/privacy') &&
    !path.startsWith('/terms') &&
    !path.startsWith('/recover_password') &&
    !path.startsWith('/change_password') &&
    !path.startsWith('/open_upload')
  ) {
    window.location.href = '/';
  }
}

/* =======================
   REQUEST → cookies + CSRF
======================= */
api.interceptors.request.use(
  async (config) => {
    // Web cookie-only: não enviar Bearer legado (backend prioriza Bearer > cookie).
    if (config.headers) {
      delete config.headers.Authorization;
      delete config.headers.authorization;
    }

    await ensureCsrfHeader(config);
    return config;
  },
  (error) => Promise.reject(error)
);

/* =======================
   RESPONSE → refresh em 401
======================= */
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config || {};
    const url = originalRequest.url || '';

    if (isPublicAuthUrl(url)) {
      return Promise.reject(error);
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    try {
      await refreshSession();
      return api(originalRequest);
    } catch (refreshError) {
      await forceLogoutRedirect();
      return Promise.reject(refreshError);
    }
  }
);

export default api;
